import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { INTEGRATION_PROVIDER_COMPOSIO } from "../../../constants";
import { hostname, parseError } from "../../../utils";
import ComposioCatalogDialog from "./ComposioCatalogDialog";

interface Tool {
  name: string;
  description: string;
}

interface ComposioIntegration {
  id: string;
  name: string;
  instanceName: string;
  type: string;
  status?: string;
  parameters?: {
    composioSlug?: string;
    composioAppSlug?: string;
    logo?: string;
  };
}

interface IntegrationWithTools extends ComposioIntegration {
  tools: Tool[];
  toolsLoaded?: boolean;
  toolsLoading?: boolean;
  error?: string;
}

interface ToolsetIntegration {
  id: string;
  name: string;
  instanceName: string;
  type: string;
  parameters?: {
    description?: string;
    tools?: Record<string, { integrationName: string; integrationType: string; tools: string[] }>;
  };
}

interface ToolsetFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  toolset: ToolsetIntegration | null;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
}

const ToolsetFormDialog: React.FC<ToolsetFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  toolset,
  companyId,
  getToken,
  eventListener,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [availableIntegrations, setAvailableIntegrations] = useState<IntegrationWithTools[]>([]);
  const [selectedTools, setSelectedTools] = useState<Record<string, Set<string>>>({});
  const [expandedIntegration, setExpandedIntegration] = useState<string | false>(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);

  // Load available Composio integrations using by-type endpoint
  const loadAvailableIntegrations = async () => {
    setLoadingTools(true);
    try {
      const token = await getToken();
      // Fetch Composio integrations by provider
      const response = await axios.get(
        `${hostname}/integrations/by-type/_`,
        {
          params: { companyId, provider: INTEGRATION_PROVIDER_COMPOSIO },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const items = response.data.items || [];
      // Filter to only healthy integrations and add empty tools array
      const integrations: IntegrationWithTools[] = items
        .filter((i: ComposioIntegration) => !i.status || i.status === "healthy")
        .map((i: ComposioIntegration) => ({
          ...i,
          tools: [],
          toolsLoaded: false,
          toolsLoading: false,
        }));
      setAvailableIntegrations(integrations);
    } catch (error) {
      console.error("Error loading Composio integrations:", error);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to load Composio integrations",
      });
    } finally {
      setLoadingTools(false);
    }
  };

  // Load tools for a specific integration when expanded
  const loadToolsForIntegration = async (integrationId: string) => {
    // Find the integration
    const integration = availableIntegrations.find((i) => i.id === integrationId);
    if (!integration || integration.toolsLoaded || integration.toolsLoading) {
      return;
    }

    // Mark as loading
    setAvailableIntegrations((prev) =>
      prev.map((i) =>
        i.id === integrationId ? { ...i, toolsLoading: true } : i
      )
    );

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/${integrationId}/tools`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const tools = response.data.tools || [];
      const error = response.data.error;

      setAvailableIntegrations((prev) =>
        prev.map((i) =>
          i.id === integrationId
            ? { ...i, tools, toolsLoaded: true, toolsLoading: false, error }
            : i
        )
      );
    } catch (error: any) {
      console.error(`Error loading tools for integration ${integrationId}:`, error);
      setAvailableIntegrations((prev) =>
        prev.map((i) =>
          i.id === integrationId
            ? { ...i, tools: [], toolsLoaded: true, toolsLoading: false, error: parseError(error) }
            : i
        )
      );
    }
  };

  useEffect(() => {
    if (open) {
      loadAvailableIntegrations();
    }
  }, [open, companyId]);

  // Load tools when an accordion is expanded
  useEffect(() => {
    if (expandedIntegration) {
      loadToolsForIntegration(expandedIntegration);
    }
  }, [expandedIntegration]);

  // Fetch full toolset data when editing (the list only has counts, not full tools config)
  const loadFullToolsetData = async (toolsetId: string) => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/${toolsetId}`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const fullToolset = response.data.integration;
      if (fullToolset?.parameters?.tools) {
        const selected: Record<string, Set<string>> = {};
        for (const [integrationId, data] of Object.entries(fullToolset.parameters.tools)) {
          selected[integrationId] = new Set((data as any).tools || []);
        }
        setSelectedTools(selected);
      }
      if (fullToolset?.parameters?.description) {
        setDescription(fullToolset.parameters.description);
      }
    } catch (error) {
      console.error("Error loading full toolset data:", error);
    }
  };

  useEffect(() => {
    if (toolset) {
      setName(toolset.instanceName || "");
      setDescription(toolset.parameters?.description || "");
      // If we have the full tools config, use it; otherwise fetch it
      if (toolset.parameters?.tools) {
        const selected: Record<string, Set<string>> = {};
        for (const [integrationId, data] of Object.entries(toolset.parameters.tools)) {
          selected[integrationId] = new Set(data.tools || []);
        }
        setSelectedTools(selected);
      } else {
        // Fetch full toolset data since the list endpoint only returns counts
        loadFullToolsetData(toolset.id);
      }
    } else {
      setName("");
      setDescription("");
      setSelectedTools({});
    }
  }, [toolset, open]);

  const handleToggleTool = (integrationId: string, toolName: string) => {
    setSelectedTools((prev) => {
      const newSelected = { ...prev };
      if (!newSelected[integrationId]) {
        newSelected[integrationId] = new Set();
      }
      const integrationTools = new Set(newSelected[integrationId]);
      if (integrationTools.has(toolName)) {
        integrationTools.delete(toolName);
      } else {
        integrationTools.add(toolName);
      }
      if (integrationTools.size === 0) {
        delete newSelected[integrationId];
      } else {
        newSelected[integrationId] = integrationTools;
      }
      return newSelected;
    });
  };

  const handleToggleAllTools = (integration: IntegrationWithTools, checked: boolean) => {
    setSelectedTools((prev) => {
      const newSelected = { ...prev };
      if (checked) {
        newSelected[integration.id] = new Set(integration.tools.map((t) => t.name));
      } else {
        delete newSelected[integration.id];
      }
      return newSelected;
    });
  };

  const isToolSelected = (integrationId: string, toolName: string): boolean => {
    return selectedTools[integrationId]?.has(toolName) || false;
  };

  const getSelectedToolCount = (integrationId: string): number => {
    return selectedTools[integrationId]?.size || 0;
  };

  const getTotalSelectedTools = (): number => {
    return Object.values(selectedTools).reduce((sum, set) => sum + set.size, 0);
  };

  const formatToolName = (name: string): string => {
    return name
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleCatalogSuccess = () => {
    setCatalogDialogOpen(false);
    // Reload integrations to include the newly added one
    loadAvailableIntegrations();
    eventListener({
      type: "SET_SUCCESS",
      message: "Integration added successfully",
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Toolset name is required",
      });
      return;
    }

    if (getTotalSelectedTools() === 0) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select at least one tool",
      });
      return;
    }

    setLoading(true);
    const currentToken = await getToken();

    // Convert selectedTools to the backend format
    const tools: Record<string, { integrationName: string; integrationType: string; tools: string[] }> = {};
    for (const [integrationId, toolSet] of Object.entries(selectedTools)) {
      const integration = availableIntegrations.find((i) => i.id === integrationId);
      if (integration) {
        const composioSlug = integration.parameters?.composioSlug || integration.parameters?.composioAppSlug || integration.type;
        tools[integrationId] = {
          integrationName: integration.instanceName || integration.name,
          integrationType: composioSlug,
          tools: Array.from(toolSet),
        };
      }
    }

    const payload = {
      companyId,
      name: name.trim(),
      description: description.trim() || null,
      tools,
    };

    try {
      if (toolset) {
        // Update existing toolset
        await axios.put(`${hostname}/integrations/toolsets/${toolset.id}`, payload, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        eventListener({
          type: "SET_SUCCESS",
          message: "Toolset updated successfully",
        });
      } else {
        // Create new toolset
        await axios.post(`${hostname}/integrations/toolsets`, payload, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        eventListener({
          type: "SET_SUCCESS",
          message: "Toolset created successfully",
        });
      }
      onSuccess();
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {toolset ? "Edit Toolset" : "Create Toolset"}
          </Typography>
          <Chip
            label={`${getTotalSelectedTools()} tools selected`}
            color={getTotalSelectedTools() > 0 ? "primary" : "default"}
          />
        </Box>
        <IconButton onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={3} py={2}>
          <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Toolset Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              placeholder="e.g., Sales Outreach Tools"
              sx={{ flex: 1 }}
              InputProps={{ sx: { fontSize: "1.1rem" } }}
              InputLabelProps={{ sx: { fontSize: "1.1rem" } }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe when to use this toolset..."
              sx={{ flex: 2 }}
              InputProps={{ sx: { fontSize: "1.1rem" } }}
              InputLabelProps={{ sx: { fontSize: "1.1rem" } }}
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Select Tools
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCatalogDialogOpen(true)}
              >
                Add Integration
              </Button>
            </Box>

              {loadingTools ? (
                <Box display="flex" alignItems="center" justifyContent="center" py={6}>
                  <CircularProgress size={32} />
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                    Loading available tools...
                  </Typography>
                </Box>
              ) : availableIntegrations.length === 0 ? (
                <Box
                  sx={{
                    p: 6,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No Composio integrations found. Connect integrations first to create toolsets.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {availableIntegrations.map((integration) => (
                    <Accordion
                      key={integration.id}
                      expanded={expandedIntegration === integration.id}
                      onChange={(_, isExpanded) =>
                        setExpandedIntegration(isExpanded ? integration.id : false)
                      }
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                          <Box
                            component="img"
                            src={integration.parameters?.logo || `https://logos.composio.dev/api/${integration.parameters?.composioSlug || integration.parameters?.composioAppSlug || integration.type}`}
                            alt={integration.type}
                            sx={{ width: 32, height: 32 }}
                            onError={(e: any) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            {integration.instanceName || integration.name}
                          </Typography>
                          <Chip
                            label={
                              integration.toolsLoaded
                                ? `${getSelectedToolCount(integration.id)}/${integration.tools.length}`
                                : `${getSelectedToolCount(integration.id)} selected`
                            }
                            variant="outlined"
                            color={getSelectedToolCount(integration.id) > 0 ? "primary" : "default"}
                            sx={{ mr: 1 }}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, py: 2 }}>
                        {integration.toolsLoading ? (
                          <Box display="flex" alignItems="center" gap={2} py={3}>
                            <CircularProgress size={24} />
                            <Typography variant="body1" color="text.secondary">
                              Loading tools...
                            </Typography>
                          </Box>
                        ) : integration.error ? (
                          <Typography variant="body1" color="error">
                            Error loading tools: {integration.error}
                          </Typography>
                        ) : integration.tools.length === 0 ? (
                          <Typography variant="body1" color="text.secondary">
                            No tools available for this integration.
                          </Typography>
                        ) : (
                          <Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    getSelectedToolCount(integration.id) === integration.tools.length
                                  }
                                  indeterminate={
                                    getSelectedToolCount(integration.id) > 0 &&
                                    getSelectedToolCount(integration.id) < integration.tools.length
                                  }
                                  onChange={(e) =>
                                    handleToggleAllTools(integration, e.target.checked)
                                  }
                                  sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                                />
                              }
                              label={
                                <Typography variant="h6" fontWeight="bold">
                                  Select All
                                </Typography>
                              }
                              sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider", pb: 1, width: "100%" }}
                            />
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                              }}
                            >
                              {integration.tools.map((tool) => (
                                <Tooltip
                                  key={tool.name}
                                  title={tool.description || ""}
                                  placement="top"
                                  arrow
                                  enterDelay={500}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isToolSelected(integration.id, tool.name)}
                                        onChange={() => handleToggleTool(integration.id, tool.name)}
                                        sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h6" fontSize="1.1rem">
                                          {formatToolName(tool.name)}
                                        </Typography>
                                        {tool.description && (
                                          <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              lineHeight: 1.5,
                                            }}
                                          >
                                            {tool.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    sx={{ display: "flex", alignItems: "flex-start", m: 0, width: "100%" }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} disabled={loading} size="large">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          disabled={loadingTools}
          size="large"
        >
          {toolset ? "Save Changes" : "Create Toolset"}
        </LoadingButton>
      </DialogActions>

      <ComposioCatalogDialog
        open={catalogDialogOpen}
        onClose={() => setCatalogDialogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        onSuccess={handleCatalogSuccess}
        onError={(error) =>
          eventListener({
            type: "SET_ERROR",
            error,
          })
        }
      />
    </Dialog>
  );
};

export default ToolsetFormDialog;
